/* =========================================================
   147 Product Add-Ons
   Dawn 15.5

   Handles:
   - Add-on carousel navigation
   - Immediate AJAX add to cart
   - Fixed quantity add-ons
   - Quantity-selectable add-ons
   - Dawn cart drawer / notification refresh

   Main product-form.js remains untouched.
   ========================================================= */

(() => {
  const ROOT_SELECTOR = '[data-product-addons]';

  class ProductAddons {
    constructor(root) {
      this.root = root;

      this.viewport = root.querySelector(
        '[data-147-addons-viewport]'
      );

      this.track = root.querySelector(
        '[data-147-addons-track]'
      );

      this.prevButton = root.querySelector(
        '[data-147-addons-prev]'
      );

      this.nextButton = root.querySelector(
        '[data-147-addons-next]'
      );

      this.dotsContainer = root.querySelector(
        '[data-147-addons-dots]'
      );

      this.currentPage = 0;
      this.pageCount = 1;
      this.scrollTimer = null;
      this.resizeTimer = null;

      /*
       * Immediate add-to-cart buttons work
       * whether or not a carousel exists.
       */
      this.bindAddToCartButtons();

      /*
       * If there is no carousel, we're done.
       */
      if (!this.viewport || !this.track) {
        return;
      }

      this.cards = Array.from(
        this.track.querySelectorAll(
          '[data-product-addon]'
        )
      );

      if (this.cards.length <= 1) {
        return;
      }

      this.onPrevClick =
        this.onPrevClick.bind(this);

      this.onNextClick =
        this.onNextClick.bind(this);

      this.onScroll =
        this.onScroll.bind(this);

      this.onResize =
        this.onResize.bind(this);

      this.prevButton?.addEventListener(
        'click',
        this.onPrevClick
      );

      this.nextButton?.addEventListener(
        'click',
        this.onNextClick
      );

      this.viewport.addEventListener(
        'scroll',
        this.onScroll,
        {
          passive: true,
        }
      );

      window.addEventListener(
        'resize',
        this.onResize
      );

      this.refresh();
    }

    /* =====================================================
       Immediate accessory add-to-cart
       ===================================================== */

    bindAddToCartButtons() {
      this.root
        .querySelectorAll(
          '[data-addon-select-button]'
        )
        .forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              this.addAddonToCart(button);
            }
          );
        });
    }

    async addAddonToCart(button) {
      const addon = button.closest(
        '[data-product-addon]'
      );

      if (!addon) {
        return;
      }

      const checkbox = addon.querySelector(
        '[data-addon-checkbox]'
      );

      if (
        !checkbox ||
        checkbox.disabled
      ) {
        return;
      }

      const variantId = Number(
        checkbox.dataset.addonVariantId
      );

      if (!variantId) {
        console.error(
          '147 Add-On: Missing variant ID.'
        );

        return;
      }

      /*
       * Fixed add-ons always add quantity 1.
       */
      let quantity = 1;

      /*
       * Quantity add-ons use their independent
       * quantity selector.
       */
      if (
        addon.dataset.addonType ===
        'quantity'
      ) {
        const quantityInput =
          addon.querySelector(
            '[data-addon-quantity-input]'
          );

        if (quantityInput) {
          quantity =
            parseInt(
              quantityInput.value,
              10
            ) || 1;

          quantity = Math.max(
            1,
            quantity
          );

          if (quantityInput.max) {
            const maxQuantity =
              parseInt(
                quantityInput.max,
                10
              );

            if (
              Number.isFinite(maxQuantity) &&
              maxQuantity > 0
            ) {
              quantity = Math.min(
                quantity,
                maxQuantity
              );
            }
          }
        }
      }

      const buttonText =
        button.querySelector(
          '[data-addon-button-text]'
        );

      /*
       * Prevent double-clicks while Shopify
       * is processing the request.
       */
      button.disabled = true;

      if (buttonText) {
        buttonText.textContent =
          'Adding…';
      }

      /*
       * IMPORTANT:
       * Keep the hidden checkbox unchecked.
       *
       * product-form.js looks for checked
       * add-on checkboxes when the MAIN product
       * is submitted.
       *
       * Since this accessory is being added
       * immediately, we do NOT want the main
       * product form adding it again later.
       */
      checkbox.checked = false;

      try {
        const cart =
          document.querySelector(
            'cart-notification'
          ) ||
          document.querySelector(
            'cart-drawer'
          );

        const payload = {
          id: variantId,
          quantity: quantity,
        };

        /*
         * Ask Shopify to return the Dawn cart
         * sections so the cart icon/drawer can
         * update immediately.
         */
        if (
          cart &&
          typeof cart.getSectionsToRender ===
            'function'
        ) {
          payload.sections =
            cart
              .getSectionsToRender()
              .map(
                (section) =>
                  section.id
              );

          payload.sections_url =
            window.location.pathname;

          if (
            typeof cart.setActiveElement ===
            'function'
          ) {
            cart.setActiveElement(
              button
            );
          }
        }

        const cartAddUrl =
          window.routes?.cart_add_url ||
          '/cart/add.js';

        const response = await fetch(
          cartAddUrl,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',

              'X-Requested-With':
                'XMLHttpRequest',
            },

            body: JSON.stringify(
              payload
            ),
          }
        );

        const responseData =
          await response.json();

        /*
         * Shopify can return JSON even when
         * the request fails, e.g. insufficient
         * inventory.
         */
        if (!response.ok) {
          throw new Error(
            responseData.description ||
              responseData.message ||
              'Unable to add this item.'
          );
        }

        /*
         * Refresh Dawn's cart drawer,
         * cart notification and cart icon count.
         */
        if (
          cart &&
          typeof cart.renderContents ===
            'function' &&
          responseData.sections
        ) {
          cart.renderContents(
            responseData
          );
        }

        /*
         * Confirmation state.
         */
        button.setAttribute(
          'aria-pressed',
          'true'
        );

        if (buttonText) {
          buttonText.textContent =
            'Added ✓';
        }

        /*
         * This isn't a permanent selected state,
         * because the product is already in the
         * Shopify cart.

         * Returning the button to Add to cart
         * also allows the customer to add another
         * one later if desired.
         */
        window.setTimeout(() => {
          button.disabled = false;

          button.setAttribute(
            'aria-pressed',
            'false'
          );

          if (buttonText) {
            buttonText.textContent =
              'Add to cart';
          }
        }, 1500);
      } catch (error) {
        console.error(
          '147 Add-On Add Error:',
          error
        );

        button.disabled = false;

        button.setAttribute(
          'aria-pressed',
          'false'
        );

        if (buttonText) {
          buttonText.textContent =
            'Add to cart';
        }

        /*
         * Use the browser alert for now so
         * inventory/errors are visible to
         * the customer rather than failing
         * silently.
         */
        window.alert(
          error.message ||
            'Sorry, this item could not be added to your cart.'
        );
      }
    }

    /* =====================================================
       Carousel
       ===================================================== */

    getStep() {
      const firstCard =
        this.cards[0];

      if (!firstCard) {
        return this.viewport.clientWidth;
      }

      const cardWidth =
        firstCard
          .getBoundingClientRect()
          .width;

      const trackStyles =
        window.getComputedStyle(
          this.track
        );

      const gap =
        parseFloat(
          trackStyles.columnGap ||
            trackStyles.gap ||
            '0'
        ) || 0;

      return cardWidth + gap;
    }

    getVisibleCardCount() {
      const step =
        this.getStep();

      if (!step) {
        return 1;
      }

      return Math.max(
        1,
        Math.floor(
          (
            this.viewport.clientWidth +
            2
          ) / step
        )
      );
    }

    calculatePageCount() {
      const visibleCards =
        this.getVisibleCardCount();

      return Math.max(
        1,
        Math.ceil(
          this.cards.length /
            visibleCards
        )
      );
    }

    getPageScrollLeft(page) {
      const visibleCards =
        this.getVisibleCardCount();

      const cardIndex =
        Math.min(
          page * visibleCards,
          this.cards.length - 1
        );

      const card =
        this.cards[cardIndex];

      return card
        ? card.offsetLeft
        : 0;
    }

    getCurrentPageFromScroll() {
      const visibleCards =
        this.getVisibleCardCount();

      const step =
        this.getStep();

      if (!step) {
        return 0;
      }

      const firstVisibleCard =
        Math.round(
          this.viewport.scrollLeft /
            step
        );

      return Math.min(
        this.pageCount - 1,
        Math.max(
          0,
          Math.floor(
            firstVisibleCard /
              visibleCards
          )
        )
      );
    }

    scrollToPage(page) {
      const targetPage =
        Math.min(
          this.pageCount - 1,
          Math.max(
            0,
            page
          )
        );

      this.viewport.scrollTo({
        left:
          this.getPageScrollLeft(
            targetPage
          ),

        behavior: 'smooth',
      });

      this.currentPage =
        targetPage;

      this.updateControls();
    }

    onPrevClick() {
      this.scrollToPage(
        this.currentPage - 1
      );
    }

    onNextClick() {
      this.scrollToPage(
        this.currentPage + 1
      );
    }

    onScroll() {
      window.clearTimeout(
        this.scrollTimer
      );

      this.scrollTimer =
        window.setTimeout(
          () => {
            this.currentPage =
              this.getCurrentPageFromScroll();

            this.updateControls();
          },
          80
        );
    }

    onResize() {
      window.clearTimeout(
        this.resizeTimer
      );

      this.resizeTimer =
        window.setTimeout(
          () => {
            this.refresh();
          },
          120
        );
    }

    buildDots() {
      if (
        !this.dotsContainer
      ) {
        return;
      }

      this.dotsContainer.innerHTML =
        '';

      if (
        this.pageCount <= 1
      ) {
        this.dotsContainer.hidden =
          true;

        return;
      }

      this.dotsContainer.hidden =
        false;

      for (
        let index = 0;
        index < this.pageCount;
        index += 1
      ) {
        const dot =
          document.createElement(
            'button'
          );

        dot.type = 'button';

        dot.className =
          'product-addons__dot';

        dot.setAttribute(
          'aria-label',
          `Show add-on page ${
            index + 1
          }`
        );

        dot.addEventListener(
          'click',
          () => {
            this.scrollToPage(
              index
            );
          }
        );

        this.dotsContainer.appendChild(
          dot
        );
      }
    }

    updateControls() {
      if (
        this.prevButton
      ) {
        this.prevButton.disabled =
          this.currentPage <= 0;
      }

      if (
        this.nextButton
      ) {
        this.nextButton.disabled =
          this.currentPage >=
          this.pageCount - 1;
      }

      if (
        this.dotsContainer
      ) {
        Array.from(
          this.dotsContainer.children
        ).forEach(
          (
            dot,
            index
          ) => {
            const active =
              index ===
              this.currentPage;

            dot.classList.toggle(
              'product-addons__dot--active',
              active
            );

            dot.setAttribute(
              'aria-current',
              active
                ? 'true'
                : 'false'
            );
          }
        );
      }
    }

    refresh() {
      this.pageCount =
        this.calculatePageCount();

      this.currentPage =
        Math.min(
          this.currentPage,
          this.pageCount - 1
        );

      this.buildDots();

      this.updateControls();

      this.root.classList.toggle(
        'product-addons--no-navigation',
        this.pageCount <= 1
      );
    }
  }

  /* =======================================================
     Initialise
     ======================================================= */

  const initAddons = (
    scope = document
  ) => {
    scope
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(
        (root) => {
          if (
            root.dataset
              .addonUiInitialized ===
            'true'
          ) {
            return;
          }

          root.dataset
            .addonUiInitialized =
            'true';

          new ProductAddons(
            root
          );
        }
      );
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAddons();
      }
    );
  } else {
    initAddons();
  }

  /*
   * Shopify theme editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAddons(
        event.target
      );
    }
  );
})();